import { PbApiError } from 'pb-api-errors';

/**
 * What a payment route's refusal says became of the money, read from its reason (app-api
 * `docs/API.md`: "A payment still in flight", the processor-failure note, and each pay route's
 * row). The reason decides because the status cannot: one 409 is a charge in flight, an invoice
 * voided before the charge, or a lock another request holds.
 *
 *  - `inFlight`: a charge may still take money. The bank is processing it, its outcome is not
 *    known, or it settled against something it does not match. Nothing is charged beside it.
 *  - `busy`: another request on the same payer holds its lock. This one charged nothing, and the
 *    other may be a payment.
 *  - `movedOn`: nothing was charged, and there is nothing left to pay from here. It was paid,
 *    accepted, voided, closed, expired or withdrawn before the charge, an earlier purchase of
 *    the payer's completed instead, or it cannot take a card.
 *  - `stillOpen`: refused before any money moved, and still payable, with another card or a
 *    second try. A replay of an attempt that failed is one: that attempt charged nothing.
 */
export type PaymentRefusalGroup = 'inFlight' | 'busy' | 'movedOn' | 'stillOpen';

/** Every reason a payment route refuses with, and its group. */
export const PAYMENT_REFUSAL_GROUPS: ReadonlyMap<string, PaymentRefusalGroup> = new Map<string, PaymentRefusalGroup>([
    ['payment_in_progress', 'inFlight'],
    ['payment_processing', 'inFlight'],
    ['attempt_unmatched', 'inFlight'],

    ['payer_busy', 'busy'],
    ['busy', 'busy'],

    ['invoice_not_payable', 'movedOn'],
    ['invoice_voided', 'movedOn'],
    ['invoice_closed', 'movedOn'],
    ['already_approved', 'movedOn'],
    ['expired', 'movedOn'],
    ['withdrawn', 'movedOn'],
    ['not_approvable_here', 'movedOn'],
    ['card_attempts_stopped', 'movedOn'],
    ['card_not_accepted', 'movedOn'],
    ['never_invoiced', 'movedOn'],
    ['earlier_purchase_completed', 'movedOn'],

    ['card_declined', 'stillOpen'],
    ['attempt_failed', 'stillOpen'],
    ['no_payment_method', 'stillOpen'],
    ['requires_action', 'stillOpen'],
    ['invalid_expiry', 'stillOpen'],
    ['card_cap_reached', 'stillOpen'],
    ['quote_stale', 'stillOpen'],
    ['proposal_changed', 'stillOpen'],
    ['validation', 'stillOpen'],
    ['processor_unavailable', 'stillOpen'],
    ['provider_unreachable', 'stillOpen'],
]);

/**
 * Where an invoice stands after a refused charge, as a pay route reports it beside the reason
 * (`errors[0].invoice_status`, app-api's company invoice pay route), and the group each status
 * means. `pending` means money may still move: the route reports an open invoice with a payment in
 * flight as `pending`, never `open`.
 */
const INVOICE_STATUS_GROUPS: ReadonlyMap<string, PaymentRefusalGroup> = new Map<string, PaymentRefusalGroup>([
    ['open', 'stillOpen'],
    ['pending', 'inFlight'],
    ['failed', 'movedOn'],
    ['voided', 'movedOn'],
    ['paid', 'movedOn'],
    ['closed', 'movedOn'],
    ['refunded', 'movedOn'],
]);

/**
 * The group a failed payment call lands in, from the most direct answer it carries: the invoice's
 * own status after the attempt, when the route reports one, else its reason, else its HTTP status.
 *
 * The status decides first because a reason alone cannot always say what the attempt left behind:
 * `Invoice::charge()` fails the invoice it charges on a processor failure that is no fault of the
 * card, so on an invoice pay route `processor_unavailable` or `no_payment_method` can come with an
 * invoice that can no longer be paid here, while on a purchase they leave nothing owed. The one
 * exception is a payer busy with another request on an invoice reported `open`: that status was
 * read before the lock's wait, so it cannot say what the request holding the lock has done since,
 * and the refusal stays `busy`. Reported `pending`, a payment on the invoice is in flight whoever
 * holds the lock.
 *
 * A reason this map does not know is `inFlight`: an open page outlives the API it was built
 * against, and a refusal it cannot read cannot vouch that nothing was charged. A failure with no
 * reason is read by its status: a 404 is the thing being paid gone, so nothing is left to pay; any
 * other 4xx is the framework's (a lapsed session, a permission, a malformed body), answered before
 * any charge; a 5xx, or a request that never got an answer, is an outcome we do not have.
 *
 * @param invoiceStatus the `invoice_status` the refusal carried, when the route reports one
 */
export function paymentRefusalGroup(error: unknown, invoiceStatus?: unknown): PaymentRefusalGroup {
    if (!(error instanceof PbApiError)) return 'inFlight';

    const byReason = error.reason !== null ? PAYMENT_REFUSAL_GROUPS.get(error.reason) ?? 'inFlight' : undefined;
    const byStatus = typeof invoiceStatus === 'string' ? INVOICE_STATUS_GROUPS.get(invoiceStatus) : undefined;
    if (byReason === 'busy' && byStatus === 'stillOpen') return 'busy';
    if (byStatus !== undefined) return byStatus;

    if (byReason !== undefined) return byReason;
    if (error.code === 404) return 'movedOn';

    return error.code >= 400 && error.code < 500 ? 'stillOpen' : 'inFlight';
}
